/**
 * Shared plant utility functions
 * Used across grid components and cell response systems
 */

/**
 * Extract genus from botanical name
 */
export const extractGenus = (botanicalName: string): string => {
  if (!botanicalName || typeof botanicalName !== 'string') return ''
  return botanicalName.trim().split(' ')[0] || ''
}

/**
 * Generate plant code from botanical name
 * Consolidated function to avoid duplicates
 */
export const generatePlantCode = (botanicalName: string): string => {
  if (!botanicalName || typeof botanicalName !== 'string') return ''
  
  const cleaned = botanicalName.trim()
  const parts = cleaned.split(' ')
  
  if (parts.length >= 2) {
    const genus = parts[0]
    const species = parts[1]
    return `${genus.substring(0, 3).toUpperCase()} ${species.substring(0, 3).toLowerCase()}`
  } else if (parts.length === 1) {
    return parts[0].substring(0, 6).toUpperCase()
  }
  
  return ''
}

/**
 * Parse cell value consistently across different value types
 */
export const parseCellValue = (val: any, field: string) => {
  if (val === null || val === undefined) return null
  
  if (typeof val === 'object' && val !== null) {
    // Handle structured cell values
    return {
      displayValue: val.label || val.value || '',
      rawValue: val.value,
      metadata: val
    }
  } else {
    // Handle primitive values
    return {
      displayValue: String(val),
      rawValue: val,
      metadata: null
    }
  }
}

/**
 * Check if a botanical name value should trigger external API calls
 */
export const shouldCallExternalApis = (sourceValue: any): boolean => {
  if (!sourceValue) {
    console.log('[shouldCallExternalApis] Skipping: no source value')
    return false
  }
  
  // Skip external API calls for free text entries to avoid unnecessary API costs
  if (typeof sourceValue === 'object' && sourceValue.source === 'free-text-entry') {
    console.log('[shouldCallExternalApis] Skipping: free text entry (avoid API costs)')
    return false
  }
  
  // Skip if the source is already from an autocomplete selection (external APIs already called)
  if (typeof sourceValue === 'object' && sourceValue.source === 'autocomplete-selection') {
    console.log('[shouldCallExternalApis] Skipping: autocomplete selection (APIs already called)')
    return false
  }
  
  const nameText = typeof sourceValue === 'object' 
    ? sourceValue.label || sourceValue.value || ''
    : sourceValue || ''
    
  const shouldCall = nameText.length > 2
  console.log(`[shouldCallExternalApis] ${shouldCall ? 'Allowing' : 'Skipping'}: "${nameText}" (length: ${nameText.length})`)
  return shouldCall
} 